/**
 * IslandPlacementPhase Component
 *
 * Renders the island placement phase (Phase 1). Players select 5 connected
 * tiles on their half of the board, then submit. Uses Board + useBoard +
 * useGameSocket.placeIslands.
 *
 * Used by:
 * - GamePage (when phase === 'ISLAND_PLACEMENT')
 *
 * Responsibilities:
 * - Display board with server state + local selected islands overlay
 * - Handle island tile selection/deselection via useBoard
 * - Validate count, half, and connectivity before submit
 * - Submit islands via socket; show status/errors
 */

import React, { useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../AuthProvider';
import { useGameStateContext } from '../context/useGameStateContext';
import useGameSocket from '../hooks/useGameSocket';
import useBoard from '../hooks/useBoard';
import { Board } from '../components/Board/Board.tsx';
import { GameProfile } from '../components/UI/GameProfile';
import { parseBoardState } from '../utils/boardHelpers';
import { IMAGES, getBackground } from '../../utils/images';
import { ISLANDS_PER_PLAYER } from '../utils/constants.ts';
import type { SquareData } from '../types/board.types';
import type { PlayerColor } from '../types/game.types';
import './GamePlay.css';

export interface IslandPlacementPhaseProps {
    /** Room code/name */
    roomCode: string;
}

/**
 * Merges server board squares with local selected islands so the UI shows
 * current selection as island tiles in the player's half.
 */
function mergeSquaresWithSelectedIslands(
    baseSquares: SquareData[],
    selectedIslands: Array<{ row: number; col: number }>,
    myPlayerLetter: 'A' | 'B'
): SquareData[] {
    if (!selectedIslands.length) return baseSquares;
    const set = new Set(selectedIslands.map((i) => `${i.row}:${i.col}`));
    return baseSquares.map((sq) => {
        if (set.has(`${sq.row}:${sq.col}`)) {
            return { ...sq, type: 'island' as const, player: myPlayerLetter };
        }
        return sq;
    });
}

export const IslandPlacementPhase: React.FC<IslandPlacementPhaseProps> = ({ roomCode: _roomCode }) => {
    const { user } = useAuth();
    const socket = useGameSocket();
    const socketRef = useRef(socket);
    socketRef.current = socket;

    const {
        board,
        myPlayerLetter,
        theme,
        playerA,
        playerB,
        myGamePlayer,
        setGameState,
    } = useGameStateContext();

    const {
        selectedIslands,
        handleSquareClick,
        validateIslandPlacement,
    } = useBoard();

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /** Base squares from server board; empty if board not yet loaded */
    const baseSquares = useMemo(() => {
        if (!board) return [];
        return parseBoardState(board);
    }, [board]);

    /** During island placement, hide opponent's islands (e.g. after reconnect) so we don't reveal their placement */
    const baseSquaresMasked = useMemo(() => {
        if (!myPlayerLetter) return baseSquares;
        return baseSquares.map((sq) => {
            if (sq.type === 'island' && sq.player !== null && sq.player !== myPlayerLetter) {
                return { ...sq, type: 'blank' as const, player: null };
            }
            return sq;
        });
    }, [baseSquares, myPlayerLetter]);

    /** Display squares: server state (opponent islands masked) plus local selection overlaid as island tiles */
    const squares = useMemo(() => {
        if (!myPlayerLetter) return baseSquaresMasked;
        return mergeSquaresWithSelectedIslands(baseSquaresMasked, selectedIslands, myPlayerLetter);
    }, [baseSquaresMasked, selectedIslands, myPlayerLetter]);

    const playerAColor: PlayerColor = (playerA?.color as PlayerColor) ?? 'red';
    const playerBColor: PlayerColor = (playerB?.color as PlayerColor) ?? 'blue';

    /** Validate and send islands to server. Keep selection visible on success (waiting for opponent). */
    const handleSubmitIslands = useCallback(() => {
        setSubmitError(null);
        const validation = validateIslandPlacement();
        if (!validation.valid) {
            setSubmitError(validation.error ?? 'Invalid placement');
            return;
        }
        setIsSubmitting(true);
        socketRef.current.placeIslands(
            { islands: selectedIslands },
            (response) => {
                setIsSubmitting(false);
                if (response.success && response.gameState) {
                    setGameState(response.gameState);
                } else if (!response.success) {
                    setSubmitError(response.error ?? 'Failed to place islands');
                }
                // Do not clear selectedIslands on success — keep islands visible while "Waiting for opponent..."
            }
        );
    }, [validateIslandPlacement, selectedIslands, setGameState]);

    const canSubmit =
        selectedIslands.length === ISLANDS_PER_PLAYER &&
        validateIslandPlacement().valid &&
        !isSubmitting;
    const alreadyPlaced = myGamePlayer?.islandsPlaced ?? false;

    if (!board) {
        return (
            <div
                className="gameplay_container"
                style={{
                    backgroundImage: `url(${getBackground(theme)})`,
                    backgroundSize: 'cover',
                }}
            >
                <p className="gameplay_status">Loading board...</p>
            </div>
        );
    }

    return (
        <div
            className="gameplay_container"
            style={{
                backgroundImage: `url(${getBackground(theme)})`,
                backgroundSize: 'cover',
            }}
        >
            <div className="top_bar_left">
                <img
                    src={IMAGES.logo}
                    className="top_bar_logo"
                    alt="Monsters of the Sea Logo"
                />
            </div>
            <GameProfile username={user.username} playerLetter={myPlayerLetter} />

            <div className="gameplay_main">
                <p className="gameplay_instructions">
                    Select {ISLANDS_PER_PLAYER} connected tiles in your half. Click to add or remove.
                </p>
                <div className="gameplay_board_wrapper">
                    <Board
                        squares={squares}
                        onSquareClick={handleSquareClick}
                        theme={theme}
                        playerLetter={myPlayerLetter}
                        playerAColor={playerAColor}
                        playerBColor={playerBColor}
                        disabled={alreadyPlaced || isSubmitting}
                    />
                </div>
                <div className="gameplay_submit_section">
                    {submitError && (
                        <p className="gameplay_status gameplay_error" role="alert">
                            {submitError}
                        </p>
                    )}
                    {alreadyPlaced ? (
                        <p className="gameplay_status">Islands placed. Waiting for opponent...</p>
                    ) : (
                        <button
                            type="button"
                            className="gameplay_button"
                            onClick={handleSubmitIslands}
                            disabled={!canSubmit}
                        >
                            {isSubmitting ? 'Placing...' : `Place islands (${selectedIslands.length}/${ISLANDS_PER_PLAYER})`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IslandPlacementPhase;
